import { z } from 'zod';
import { isEmailAddress } from './emailAddress.js';
import { SEND_BODY_MAX, SEND_TITLE_MAX } from './automations/actions.js';
export const DESTINATION_KINDS = [
    'discord',
    'json_webhook',
    'ntfy',
    'gotify',
    'apprise',
    'pushover',
    'email',
    'push',
    'web_toast',
];
export const NOTIFICATION_EVENT_TYPES = [
    'violation_detected',
    'stream_started',
    'stream_stopped',
    'server_down',
    'server_up',
    'plugin_update_available',
    'server_update_available',
    'tracearr_update_available',
    'media_added',
    'media_upgraded',
    'new_device',
    'trust_score_changed',
    'newsletter_send',
];
/** What a destination may subscribe to on its own; every other event reaches it through an automation. */
export const SUBSCRIBABLE_EVENTS = [
    'violation_detected',
];
const ALL_EVENTS = NOTIFICATION_EVENT_TYPES;
/** Push has no plugin-update method and the browser has no toast for it. */
const EVENTS_WITHOUT_PLUGIN = Object.freeze(NOTIFICATION_EVENT_TYPES.filter((e) => e !== 'plugin_update_available'));
const url = (key, label, placeholder) => ({
    key,
    label,
    input: 'url',
    required: true,
    secret: true,
    placeholder,
});
const secret = (key, label, required, hint) => ({
    key,
    label,
    input: 'secret',
    required,
    secret: true,
    hint,
});
const text = (key, label, required, placeholder, def) => ({
    key,
    label,
    input: 'text',
    required,
    secret: false,
    placeholder,
    default: def,
});
export const EMAIL_SECURITY = ['starttls', 'tls', 'none'];
/** Host, port and security per provider; the dialog copies them into the sibling fields. usernameHint/passwordHint are i18n keys under pages:settings.destinations.hints, resolved by the dialog rather than copied into the form. */
export const EMAIL_SMTP_PRESETS = {
    postmark: {
        host: 'smtp.postmarkapp.com',
        port: '587',
        security: 'starttls',
        usernameHint: 'smtpPostmarkUsername',
        passwordHint: 'smtpPostmarkPassword',
    },
    resend: {
        host: 'smtp.resend.com',
        port: '465',
        security: 'tls',
        usernameHint: 'smtpResendUsername',
        passwordHint: 'smtpResendPassword',
    },
    ses: {
        host: 'email-smtp.us-east-1.amazonaws.com',
        port: '587',
        security: 'starttls',
        usernameHint: 'smtpSesUsername',
        passwordHint: 'smtpSesPassword',
    },
    mailgun: {
        host: 'smtp.mailgun.org',
        port: '587',
        security: 'starttls',
        usernameHint: 'smtpMailgunUsername',
        passwordHint: 'smtpMailgunPassword',
    },
    sendgrid: {
        host: 'smtp.sendgrid.net',
        port: '587',
        security: 'starttls',
        usernameHint: 'smtpSendgridUsername',
        passwordHint: 'smtpSendgridPassword',
    },
    brevo: {
        host: 'smtp-relay.brevo.com',
        port: '587',
        security: 'starttls',
        usernameHint: 'smtpBrevoUsername',
        passwordHint: 'smtpBrevoPassword',
    },
    gmail: {
        host: 'smtp.gmail.com',
        port: '587',
        security: 'starttls',
        usernameHint: 'smtpGmailUsername',
        passwordHint: 'smtpGmailPassword',
    },
};
const select = (key, label, options, def, extra = {}) => ({
    key,
    label,
    input: 'select',
    required: true,
    secret: false,
    options,
    default: def,
    ...extra,
});
const number = (key, label, def, min, max, hint) => ({
    key,
    label,
    input: 'number',
    required: true,
    secret: false,
    default: def,
    min,
    max,
    hint,
});
const email = (key, label, required, placeholder) => ({
    key,
    label,
    input: 'email',
    required,
    secret: false,
    placeholder,
});
const emails = (key, label, required, placeholder, hint) => ({
    key,
    label,
    input: 'emails',
    required,
    secret: false,
    placeholder,
    hint,
});
const toggle = (key, label, def, hint) => ({
    key,
    label,
    input: 'toggle',
    required: false,
    secret: false,
    default: def,
    hint,
});
const grouped = (group, fields) => fields.map((field) => ({ ...field, group }));
const PRESET_OPTIONS = [
    { value: 'custom', label: 'presetCustom' },
    { value: 'postmark', label: 'presetPostmark' },
    { value: 'resend', label: 'presetResend' },
    { value: 'ses', label: 'presetSes' },
    { value: 'mailgun', label: 'presetMailgun' },
    { value: 'sendgrid', label: 'presetSendgrid' },
    { value: 'brevo', label: 'presetBrevo' },
    { value: 'gmail', label: 'presetGmail' },
];
const SECURITY_OPTIONS = [
    { value: 'starttls', label: 'securityStarttls' },
    { value: 'tls', label: 'securityTls' },
    { value: 'none', label: 'securityNone' },
];
export const DESTINATION_TYPES = {
    discord: {
        kind: 'discord',
        label: 'discord',
        icon: 'MessageSquare',
        builtin: false,
        events: ALL_EVENTS,
        fields: [url('webhookUrl', 'webhookUrl', 'https://discord.com/api/webhooks/...')],
    },
    json_webhook: {
        kind: 'json_webhook',
        label: 'jsonWebhook',
        icon: 'Webhook',
        builtin: false,
        events: ALL_EVENTS,
        fields: [url('url', 'url', 'https://example.com/webhook')],
    },
    ntfy: {
        kind: 'ntfy',
        label: 'ntfy',
        icon: 'Bell',
        builtin: false,
        events: ALL_EVENTS,
        fields: [
            url('url', 'serverUrl', 'https://ntfy.sh/'),
            text('topic', 'topic', true, 'tracearr', 'tracearr'),
            secret('authToken', 'authToken', false, 'authTokenOptional'),
        ],
    },
    gotify: {
        kind: 'gotify',
        label: 'gotify',
        icon: 'Bell',
        builtin: false,
        events: ALL_EVENTS,
        fields: [url('url', 'serverUrl', 'https://gotify.example.com/message?token=...')],
    },
    apprise: {
        kind: 'apprise',
        label: 'apprise',
        icon: 'Share2',
        builtin: false,
        events: ALL_EVENTS,
        fields: [url('url', 'apiUrl', 'https://apprise.example.com/notify/apprise')],
    },
    pushover: {
        kind: 'pushover',
        label: 'pushover',
        icon: 'Smartphone',
        builtin: false,
        events: ALL_EVENTS,
        fields: [secret('userKey', 'userKey', true), secret('apiToken', 'apiToken', true)],
    },
    email: {
        kind: 'email',
        label: 'email',
        icon: 'Mail',
        builtin: false,
        events: ALL_EVENTS,
        fields: [
            ...grouped('connection', [
                select('preset', 'preset', PRESET_OPTIONS, 'custom', {
                    hint: 'smtpPreset',
                    presets: EMAIL_SMTP_PRESETS,
                }),
                text('host', 'host', true, 'smtp.example.com'),
                number('port', 'port', '587', 1, 65535),
                select('security', 'security', SECURITY_OPTIONS, 'starttls'),
                toggle('verifyCertificate', 'verifyCertificate', 'true', 'smtpVerifyCertificate'),
                { ...text('username', 'username', false), hint: 'smtpUsernameOptional' },
                secret('password', 'password', false),
                number('messagesPerSecond', 'messagesPerSecond', '2', 1, 50, 'smtpRate'),
                {
                    ...text('messageStream', 'messageStream', false),
                    hint: 'smtpMessageStream',
                    maxLength: 64,
                    pattern: /^[A-Za-z0-9-]*$/,
                },
            ]),
            ...grouped('sender', [
                text('fromName', 'fromName', false, undefined, 'Tracearr'),
                email('fromAddress', 'fromAddress', true, 'tracearr@example.com'),
                email('replyTo', 'replyTo', false),
            ]),
            ...grouped('alerts', [emails('to', 'to', false, 'you@example.com', 'smtpTo')]),
        ],
    },
    push: {
        kind: 'push',
        label: 'push',
        icon: 'Smartphone',
        builtin: true,
        events: EVENTS_WITHOUT_PLUGIN,
        fields: [],
    },
    web_toast: {
        kind: 'web_toast',
        label: 'webToast',
        icon: 'Globe',
        builtin: true,
        events: EVENTS_WITHOUT_PLUGIN,
        fields: [],
    },
};
const plain = (title = { max: SEND_TITLE_MAX, unit: 'chars' }, body = { max: SEND_BODY_MAX, unit: 'chars' }) => ({ escape: 'none', title, body });
export const DESTINATION_TEXT_PROFILES = {
    discord: {
        escape: 'discordMarkdown',
        title: { max: 256, unit: 'chars' },
        body: { max: 4096, unit: 'chars' },
    },
    json_webhook: { escape: 'none', title: null, body: null },
    ntfy: plain(undefined, { max: 4096, unit: 'bytes' }),
    gotify: plain(),
    apprise: plain(),
    pushover: plain({ max: 250, unit: 'chars' }, { max: 1024, unit: 'chars' }),
    email: plain(),
    push: plain(undefined, { max: 1024, unit: 'chars' }),
    web_toast: plain(),
};
const DISCORD_SPECIAL = /[\\*_~`|>#\-[\]()]/g;
const URL_RUN = /https?:\/\/\S+/g;
/** Backslashes inside a URL turn into slashes in Discord's autolink, so URLs pass through. */
export function escapeDiscordMarkdown(value) {
    let out = '';
    let last = 0;
    for (const match of value.matchAll(URL_RUN)) {
        out += value.slice(last, match.index).replace(DISCORD_SPECIAL, '\\$&');
        out += match[0];
        last = match.index + match[0].length;
    }
    return out + value.slice(last).replace(DISCORD_SPECIAL, '\\$&');
}
const identity = (value) => value;
export function escapeFor(profile) {
    return profile.escape === 'discordMarkdown' ? escapeDiscordMarkdown : identity;
}
const httpUrl = z
    .string()
    .trim()
    .refine((v) => /^https?:\/\/\S+$/i.test(v), 'Must be an http(s) URL');
export function addressList(value) {
    return value
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part !== '');
}
/** An optional field may be left blank; a required one gets `.min(1)` after this, so blank fails there. */
function fieldSchema(f) {
    const blankOk = (check) => (v) => (!f.required && v === '') || check(v);
    switch (f.input) {
        case 'url':
            return httpUrl;
        case 'email':
            return z.string().trim().max(254).refine(blankOk(isEmailAddress), 'Must be an email address');
        case 'emails':
            return z
                .string()
                .trim()
                .max(2000)
                .refine(blankOk((v) => addressList(v).length > 0 && addressList(v).every(isEmailAddress)), 'Must be one or more comma-separated email addresses');
        case 'number': {
            const min = f.min ?? Number.MIN_SAFE_INTEGER;
            const max = f.max ?? Number.MAX_SAFE_INTEGER;
            return z
                .string()
                .trim()
                .refine(blankOk((v) => /^\d+$/.test(v) && Number(v) >= min && Number(v) <= max), `Must be a whole number between ${min} and ${max}`);
        }
        case 'select': {
            const allowed = (f.options ?? []).map((o) => o.value);
            return z.string().refine(blankOk((v) => allowed.includes(v)), 'Must be one of the listed options');
        }
        case 'toggle':
            return z.string().refine(blankOk((v) => v === 'true' || v === 'false'), 'Must be true or false');
        case 'text':
        case 'secret': {
            const { pattern } = f;
            const base = z
                .string()
                .trim()
                .max(f.maxLength ?? 2000);
            return pattern
                ? base.refine(blankOk((v) => pattern.test(v)), 'Invalid format')
                : base;
        }
    }
}
/** Zod object for a field list; unknown keys rejected. Exported so refinements are testable without a kind. */
export function configSchemaForFields(fields) {
    const shape = {};
    for (const f of fields) {
        let s = fieldSchema(f);
        if (f.required)
            s = s.min(1);
        shape[f.key] =
            f.default !== undefined ? s.default(f.default) : f.required ? s : s.optional().nullable();
    }
    return z.strictObject(shape);
}
/** Zod object for one kind's config, built from its descriptor. */
export function destinationConfigSchema(kind) {
    return configSchemaForFields(DESTINATION_TYPES[kind].fields);
}
export const notificationEventTypeSchema = z.enum(NOTIFICATION_EVENT_TYPES);
const subscribableEventSchema = z.enum(SUBSCRIBABLE_EVENTS);
const nonBuiltinKind = z.enum(DESTINATION_KINDS.filter((k) => !DESTINATION_TYPES[k].builtin));
export const createDestinationSchema = z.strictObject({
    name: z.string().trim().min(1).max(100),
    type: nonBuiltinKind,
    config: z.record(z.string(), z.unknown()),
    events: z.array(subscribableEventSchema).default([]),
    enabled: z.boolean().default(true),
});
/** Secrets: omitted keeps the stored value, null clears, a string replaces. */
export const updateDestinationSchema = z.strictObject({
    name: z.string().trim().min(1).max(100).optional(),
    config: z.record(z.string(), z.union([z.string(), z.null()])).optional(),
    events: z.array(subscribableEventSchema).optional(),
    enabled: z.boolean().optional(),
});
//# sourceMappingURL=destinations.js.map